import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import EnhancedLineChart, { DataPointClickEvent } from './EnhancedLineChart';
import { InlineValueEditor } from './InlineValueEditor';
import { TestSelector } from './trends/TestSelector';
import { DateRangeSelector } from './trends/DateRangeSelector';
import { TrendsInsights } from './trends/TrendsInsights';
import { ReferenceRangeToggle } from './ReferenceRangeToggle';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { apiService } from '../services/apiService';
import { 
  TestTrend, 
  DateRange, 
  TrendsResponse, 
  SelectableTest,
  TrendDataPoint 
} from '../types/trends';
import './InlineValueEditor.css';
import { ReferenceRangeService } from '../services/referenceRangeService';
import { debugReferenceRanges } from '../utils/debugReferenceRanges';
import { UserDemographics, UserDemographicsData } from './UserDemographics';
import { detectLikelyUnit, analyzeLabUnitConsistency, LabAnalysisResult, roundValue, convertValue } from '../utils/unitConverter';
import { DataValidationService } from '../services/dataValidationService';
import { UnitMismatchWarning } from './UnitMismatchWarning';
import { CorrectionDialog } from './CorrectionDialog';
// import { OutlierAlert } from './OutlierAlert'; // Removed - redundant with auto-correction
import { detectUnitErrors } from '../services/unitErrorDetection';
import { AutoCorrectionManager } from './AutoCorrectionManager';
import { debugAutoCorrection } from '../utils/debugAutoCorrection';
import { debugDataLoading, resetLoadCounter } from '../utils/debugDataLoading';
import { detectOutliers, getChartDisplayBounds, EnhancedDataPoint } from '../services/outlierDetectionService';
import { isValidUnitForTest, detectLikelyUnitError } from '../utils/labUnitValidation';
import { SinglePointReferenceDisplay } from './SinglePointReferenceDisplay';
import { FloatingChatModal } from './FloatingChatModal';
import { DateSelector } from './DateSelector';
import { getTestDescription } from '../data/testDescriptions';
import { calculateStatus } from '../utils/statusCalculator';
import { useTrendsCache } from '../contexts/TrendsDataContext';
import { TrendsCache } from '../utils/trendsCache';
import LLMExplanation from './LLMExplanation';

// Add global function for review button interface
declare global {
  interface Window {
    reviewAutoCorrections?: (testName?: string) => void;
  }
}

interface TrendsViewProps {
  onClose?: () => void;
}

export const TrendsView: React.FC<TrendsViewProps> = ({ onClose }) => {
  const { user, isAuthenticated } = useSimpleAuth();
  const sessionId = user?.id.toString() || 'anonymous';
  
  // Load saved tests from localStorage
  const getSavedTests = useCallback((): string[] => {
    try {
      const saved = localStorage.getItem('trendsView_selectedTests');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);
  
  const [selectedTests, setSelectedTests] = useState<string[]>(getSavedTests());
  const [availableTests, setAvailableTests] = useState<SelectableTest[]>([]);
  const [trendsData, setTrendsData] = useState<Record<string, TestTrend>>({});
  
  // Use global cache from context
  const { getCachedTrend, setCachedTrend } = useTrendsCache();
  
  // Save selected tests to localStorage whenever they change
  useEffect(() => {
    if (selectedTests.length > 0) {
      localStorage.setItem('trendsView_selectedTests', JSON.stringify(selectedTests));
    }
  }, [selectedTests]);
  const [dateRange, setDateRange] = useState<DateRange>({
    preset: 'all',
    label: 'All time'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousSelectedTestsRef = useRef<string[]>([]);
  const previousDateRangeRef = useRef<DateRange>(dateRange);
  const previousReferenceSourceRef = useRef<string>('enhanced');
  const loadingTestsRef = useRef<Set<string>>(new Set());
  const [loadingTests, setLoadingTests] = useState<Set<string>>(new Set());
  const [editingPoint, setEditingPoint] = useState<{
    testName: string;
    dataIndex: number;
    value: number;
    date: string;
    units: string;
    originalValue?: number;
    originalUnits?: string;
    availableUnits?: string[];
    position: { x: number; y: number };
    referenceRange?: { min?: number; max?: number };
    reportId?: number;
    resultId?: number;
    isSuspicious?: boolean;
    validationMessage?: string;
  } | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);
  const [loadingReferenceRanges, setLoadingReferenceRanges] = useState<Set<string>>(new Set());
  
  // LLM explanation state
  const [selectedMarkerForExplanation, setSelectedMarkerForExplanation] = useState<string | null>(null);
  const [llmExplanation, setLlmExplanation] = useState<string | null>(null);
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);
  const [explanationError, setExplanationError] = useState<string | null>(null);
  const [explanationValue, setExplanationValue] = useState<number | undefined>(undefined);
  const [explanationUnits, setExplanationUnits] = useState<string | undefined>(undefined);
  const [referenceRangeSources, setReferenceRangeSources] = useState<Record<string, 'original' | 'enhanced'>>({});
  const [referenceRangeDetails, setReferenceRangeDetails] = useState<Record<string, { source?: string; notes?: string }>>({});
  const [userDemographics, setUserDemographics] = useState<UserDemographicsData>({});
  const [unitMismatches, setUnitMismatches] = useState<Map<string, {
    conversions: Array<{
      fromValue: number;
      fromUnit: string;
      toValue: number;
      toUnit: string;
      date: string;
      isMislabeled?: boolean;
      reason?: string;
    }>;
    warnings?: string[];
  }>>(new Map());
  const [showUnitMismatchWarning, setShowUnitMismatchWarning] = useState<string | null>(null);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState<{
    testName: string;
    dataPoint: any;
    unitMismatchInfo?: {
      detectedUnit: string;
      reason: string;
      suggestedValue: number;
    };
  } | null>(null);
  const [labAnalysisResult, setLabAnalysisResult] = useState<LabAnalysisResult | null>(null);
  const [showLabIssuesBanner, setShowLabIssuesBanner] = useState(true);
  const [referenceRangeSource, setReferenceRangeSource] = useState<'lab' | 'standard'>('standard');
  const [showReferenceRanges, setShowReferenceRanges] = useState(true);
  // Auto-correction management
  const [autoCorrectedValues, setAutoCorrectedValues] = useState<Map<string, Array<{
    date: string;
    originalValue: number;
    originalUnits: string;
    correctedValue: number;
    correctedUnits: string;
    reason: string;
  }>>>(new Map());
  const [showCorrectionManager, setShowCorrectionManager] = useState(false);
  const [correctionManagerTestFilter, setCorrectionManagerTestFilter] = useState<string | null>(null);
  // Track values marked as "verified correct" by the user
  const [verifiedValues, setVerifiedValues] = useState<Map<string, Set<string>>>(new Map()); // testName -> Set of dates
  
  // New states for view management
  const [viewMode, setViewMode] = useState<'auto' | 'single' | 'trends'>('auto');
  const [selectedSingleDate, setSelectedSingleDate] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);


  // Determine effective view mode based on data - memoized for performance
  const effectiveViewMode = useMemo(() => {
    if (viewMode !== 'auto') return viewMode;
    
    // Count unique dates across all selected tests
    const allDates = new Set<string>();
    selectedTests.forEach(testName => {
      const trend = trendsData[testName];
      if (trend?.dataPoints) {
        trend.dataPoints.forEach(dp => allDates.add(dp.date));
      }
    });
    
    const uniqueDateCount = allDates.size;
    
    // Auto-detection logic
    if (uniqueDateCount === 0) return 'trends';
    if (uniqueDateCount <= 3) return 'single';
    return 'trends';
  }, [viewMode, selectedTests, trendsData]);
  
  // Get all available dates for single view navigation - memoized for performance
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    selectedTests.forEach(testName => {
      const trend = trendsData[testName];
      if (trend?.dataPoints) {
        trend.dataPoints.forEach(dp => dates.add(dp.date));
      }
    });
    return Array.from(dates).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  }, [selectedTests, trendsData]);


  // Get data for single date view
  const getSingleDateData = useCallback(() => {
    if (!selectedSingleDate) return {};
    
    const singleDateData: Record<string, any> = {};
    selectedTests.forEach(testName => {
      const trend = trendsData[testName];
      if (trend?.dataPoints) {
        const dataPoint = trend.dataPoints.find(dp => dp.date === selectedSingleDate);
        if (dataPoint) {
          singleDateData[testName] = {
            value: dataPoint.value,
            units: dataPoint.units,
            referenceMin: dataPoint.referenceMin,
            referenceMax: dataPoint.referenceMax,
            status: dataPoint.status,
            date: dataPoint.date,
            displayName: trend.displayName
          };
        }
      }
    });
    return singleDateData;
  }, [selectedSingleDate, selectedTests, trendsData]);

  const calculateDays = (range: DateRange): number => {
    switch (range.preset) {
      case 'last3months': return 90;
      case 'last6months': return 180;
      case 'last1year': return 365;
      case 'last2years': return 730;
      case 'all': return 7300; // 20 years - ensures we get all historical data
      default: {
        if (range.startDate && range.endDate) {
          const start = new Date(range.startDate);
          const end = new Date(range.endDate);
          return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        }
        return 365;
      }
    }
  };

  // Format test names for display
  // Helper function to check if zero is valid for a test
  const isZeroValidForTest = (testName: string): boolean => {
    const validZeroTests = [
      'BASOPHIL', 'EOSINOPHIL', 'RETICULOCYTE', 'RETIC',
      'IGE', 'IGG', 'IGM', 'IGA', 'ANTIBODY', 'TITER',
      'CEA', 'AFP', 'CA125', 'CA19-9', 'PSA',
      'ETHANOL', 'ALCOHOL', 'DRUG', 'COCAINE', 'MARIJUANA', 'THC',
      'HCG', 'BHCG', 'PROGESTERONE', 'ESTRADIOL'
    ];
    
    const testUpper = testName.toUpperCase();
    return validZeroTests.some(validTest => 
      testUpper.includes(validTest) || validTest.includes(testUpper)
    );
  };

  // Format test names for display
  const formatTestName = (testName: string): string => {
    // Handle common abbreviations
    const abbreviations: Record<string, string> = {
      'wbc': 'WBC',
      'rbc': 'RBC',
      'hdl': 'HDL',
      'ldl': 'LDL',
      'alt': 'ALT',
      'ast': 'AST',
      'tsh': 'TSH',
      'mcv': 'MCV',
      'mch': 'MCH',
      'mchc': 'MCHC',
      'rdw': 'RDW',
      'mpv': 'MPV',
      'bun': 'BUN',
      'egfr': 'eGFR',
      'a1c': 'A1C',
      'hba1c': 'HbA1c',
      't3': 'T3',
      't4': 'T4',
      'b12': 'B12',
      'psa': 'PSA',
      'crp': 'CRP',
      'esr': 'ESR'
    };
    
    // Check if it's a known abbreviation
    const lowerName = testName.toLowerCase();
    if (abbreviations[lowerName]) {
      return abbreviations[lowerName];
    }
    
    // Otherwise, capitalize first letter of each word
    return testName
      .split(/[_\s-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const loadAvailableTests = useCallback(async () => {
    if (!user || !isAuthenticated) {
      console.log('No auth available for loading tests');
      return;
    }

    console.log('Loading available tests with auth:', user.email);

    try {
      // Get actual unique test names from the database
      const response = await apiService.getUniqueTests();

      if (response.status === 'success') {
        const uniqueTests = response.data;
        
        // Create SelectableTest objects from actual database test names
        const availableTestsList: SelectableTest[] = uniqueTests.map((testName: string) => ({
          testName: testName,
          displayName: formatTestName(testName),
          units: '', // Will be populated when data is loaded
          available: true,
          lastValue: undefined,
          lastDate: undefined
        }));

        setAvailableTests(availableTestsList);
        
        // Log available tests for debugging
        console.log(`Found ${availableTestsList.length} unique tests:`, uniqueTests);
        
        // Check if we have saved tests or need to auto-select
        if (availableTestsList.length > 0) {
          const savedTests = getSavedTests();
          
          if (savedTests.length > 0) {
            // Filter saved tests to only include those that are actually available
            const validSavedTests = savedTests.filter(testName => 
              availableTestsList.some(test => test.testName === testName)
            );
            
            if (validSavedTests.length > 0) {
              setSelectedTests(validSavedTests);
              console.log('Restored saved tests:', validSavedTests);
            } else {
              // Saved tests are no longer available, auto-select new ones
              const autoSelectTests = availableTestsList
                .slice(0, 3)
                .map(test => test.testName);
              setSelectedTests(autoSelectTests);
              console.log('Auto-selected tests (saved tests unavailable):', autoSelectTests);
            }
          } else if (selectedTests.length === 0) {
            // No saved tests and no current selection, auto-select
            const autoSelectTests = availableTestsList
              .slice(0, 3)
              .map(test => test.testName);
            setSelectedTests(autoSelectTests);
            console.log('Auto-selected tests:', autoSelectTests);
          }
        }
        
        // If we don't have any tests from the database, show common ones as placeholders
        if (availableTestsList.length === 0) {
          const commonTests: SelectableTest[] = [
          // Blood count tests
          { testName: 'hemoglobin', displayName: 'Hemoglobin', units: 'g/dL', available: true },
          { testName: 'hematocrit', displayName: 'Hematocrit', units: '%', available: true },
          { testName: 'wbc', displayName: 'White Blood Cells', units: 'K/uL', available: true },
          { testName: 'platelet_count', displayName: 'Platelets', units: 'K/uL', available: true },
          { testName: 'rbc', displayName: 'Red Blood Cells', units: 'M/uL', available: true },
          
          // Chemistry tests
          { testName: 'glucose', displayName: 'Glucose', units: 'mg/dL', available: true },
          { testName: 'cholesterol_total', displayName: 'Total Cholesterol', units: 'mg/dL', available: true },
          { testName: 'cholesterol', displayName: 'Cholesterol', units: 'mg/dL', available: true },
          { testName: 'ldl', displayName: 'LDL Cholesterol', units: 'mg/dL', available: true },
          { testName: 'hdl', displayName: 'HDL Cholesterol', units: 'mg/dL', available: true },
          { testName: 'triglycerides', displayName: 'Triglycerides', units: 'mg/dL', available: true },
          
          // Kidney function
          { testName: 'creatinine', displayName: 'Creatinine', units: 'mg/dL', available: true },
          { testName: 'bun', displayName: 'BUN', units: 'mg/dL', available: true },
          { testName: 'egfr', displayName: 'eGFR', units: 'mL/min/1.73m2', available: true },
          
          // Liver function
          { testName: 'alt', displayName: 'ALT (Liver)', units: 'U/L', available: true },
          { testName: 'ast', displayName: 'AST', units: 'U/L', available: true },
          { testName: 'alkaline_phosphatase', displayName: 'Alkaline Phosphatase', units: 'U/L', available: true },
          
          // Thyroid
          { testName: 'tsh', displayName: 'TSH (Thyroid)', units: 'mIU/L', available: true },
          { testName: 't3', displayName: 'T3', units: 'ng/dL', available: true },
          { testName: 't4', displayName: 'T4', units: 'μg/dL', available: true },
          
          // Other common tests
          { testName: 'vitamin_d', displayName: 'Vitamin D', units: 'ng/mL', available: true },
          { testName: 'ferritin', displayName: 'Ferritin', units: 'ng/mL', available: true },
          { testName: 'b12', displayName: 'Vitamin B12', units: 'pg/mL', available: true },
          { testName: 'psa', displayName: 'PSA', units: 'ng/mL', available: true },
        ];

          setAvailableTests(commonTests);
        }
      }
    } catch (error: any) {
      console.error('Error loading available tests:', error);
      
      // Don't show error if it's a 401 - let the session handler deal with it
      if (error.response?.status === 401) {
        // Session handler will retry the request
        return;
      } else {
        setError('Failed to load available tests');
      }
    }
  }, [user, isAuthenticated]);

  // Load trends data for specific tests (or all if testsToLoad is not provided)
  const loadTrendsData = useCallback(async (testsToLoad?: string[]) => {
    if (!user || !isAuthenticated) return;
    
    // If no specific tests provided, load all selected tests
    const actualTestsToLoad = testsToLoad || selectedTests;
    if (actualTestsToLoad.length === 0) return;

    // Check cache and filter out tests that are already loading or cached
    const testsToFetch: string[] = [];
    const cachedResults: Record<string, TestTrend> = {};
    
    for (const test of actualTestsToLoad) {
      // Skip if already loading
      if (loadingTestsRef.current.has(test)) {
        continue;
      }
      
      // Check global cache
      const cacheKey = dateRange.preset || `custom_${dateRange.startDate}_${dateRange.endDate}`;
      const cached = getCachedTrend(test, cacheKey, referenceRangeSource);
      
      if (cached) {
        // Use cached data
        cachedResults[test] = cached;
      } else {
        // Need to fetch
        testsToFetch.push(test);
      }
    }
    
    // Update state with cached results immediately
    if (Object.keys(cachedResults).length > 0) {
      setTrendsData(prev => ({ ...prev, ...cachedResults }));
    }
    
    // If all tests were cached or loading, we're done
    if (testsToFetch.length === 0) {
      console.log('All requested tests are cached or loading, skipping API call');
      return;
    }

    // Mark tests as loading
    testsToFetch.forEach(test => loadingTestsRef.current.add(test));
    setLoadingTests(new Set(loadingTestsRef.current));

    debugDataLoading('loadTrendsData called', {
      user: user?.email,
      testsToLoad: testsToFetch,
      isIncremental: !!testsToLoad,
      dateRange: dateRange.label
    });

    // Only show loading state if loading all tests
    if (!testsToLoad) {
      setLoading(true);
    }
    setError(null);

    // Declare transformedData outside try block so it's accessible in finally
    let transformedData: Record<string, TestTrend> = {};

    try {
      // Calculate days based on date range
      const days = calculateDays(dateRange);
      
      // Build params with actual dates if custom range
      const params: any = {
        tests: testsToFetch,
        days: days,
        rangeSource: referenceRangeSource
      };

      // Add actual date range for custom dates
      if (dateRange.preset === 'custom' && dateRange.startDate && dateRange.endDate) {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      
      // Only log important events
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Trends] Loading ${testsToFetch.length} tests`);
      }

      // Manually build the query string to ensure proper format
      const queryParams = new URLSearchParams();
      queryParams.append('days', params.days.toString());
      queryParams.append('rangeSource', params.rangeSource);
      
      // Add user demographics if available
      if (userDemographics.age) {
        queryParams.append('age', userDemographics.age.toString());
      }
      if (userDemographics.sex) {
        queryParams.append('sex', userDemographics.sex);
      }
      
      // Add each test with brackets
      testsToFetch.forEach((test: string) => {
        queryParams.append('tests[]', test);
      });
      
      console.log('Trends API URL:', `/api/trends?${queryParams.toString()}`);
      
      const response = await apiService.getTrends(queryParams);
      console.log('Trends API Response:', response.data);
      
      // Defensive check: ensure response.data exists
      if (!response.data) {
        console.error('Invalid response format: missing data property');
        setError('Invalid response format from server');
        return;
      }
      
      // Debug: Check if we got reference ranges
      if (response.data?.trends) {
        Object.entries(response.data.trends).forEach(([testName, points]: [string, any]) => {
          if (testName.toLowerCase().includes('hdl') && Array.isArray(points) && points.length > 0) {
            console.log(`🎯 HDL API Response - First point:`, points[0]);
          }
        });
      }

      if (response.status === 'success') {
        // Backend returns trends in data.trends
        const rawData = response.data.trends || {};
        
        // First, analyze lab-level unit consistency
        const allLabResults: Array<{
          reportId: string;
          reportDate: string;
          testName: string;
          value: number;
          units: string;
        }> = [];
        
        // Collect all lab results for analysis
        Object.entries(rawData).forEach(([testName, dataPoints]) => {
          if (Array.isArray(dataPoints)) {
            dataPoints.forEach(dp => {
              allLabResults.push({
                reportId: dp.reportId?.toString() || '',
                reportDate: dp.date,
                testName,
                value: dp.value,
                units: dp.units || ''
              });
            });
          }
        });
        
        // Analyze for systematic unit issues
        const labAnalysis = analyzeLabUnitConsistency(allLabResults);
        setLabAnalysisResult(labAnalysis);
        
        // Log lab analysis results
        if (labAnalysis.systematicIssues.length > 0) {
          console.group('🔍 Lab Unit Analysis Results');
          console.log('Systematic issues found:', labAnalysis.systematicIssues);
          console.log('Lab-specific issues:', Array.from(labAnalysis.labIssues.entries()));
          console.log('Overall confidence:', labAnalysis.overallConfidence);
          console.groupEnd();
        }
        
        // Transform raw data into TestTrend format
        transformedData = {};
        
        // Process each test's data
        await Promise.all(Object.entries(rawData).map(async ([testName, dataPoints]) => {
          const test = availableTests.find(t => t.testName === testName);
          
          // Only add if we have data points
          if (Array.isArray(dataPoints) && dataPoints.length > 0) {
            // Check if any data point has reference ranges
            const hasOriginalReference = dataPoints.some(
              point => point.referenceMin !== null && point.referenceMax !== null
            );
            
            // The backend already handles unit normalization, so we just need to check if any conversions happened
            const hasConversions = dataPoints.some(dp => dp.originalUnits && dp.originalUnits !== dp.units);
            const targetUnit = dataPoints[0]?.units || '';  // All should be normalized to same unit by backend
            
            // Track unit conversions for the warning banner
            if (hasConversions) {
              const conversions = dataPoints
                .filter(dp => dp.originalUnits && dp.originalUnits !== dp.units)
                .map(dp => ({
                  fromValue: dp.originalValue || dp.value,
                  fromUnit: dp.originalUnits!,
                  toValue: dp.value,
                  toUnit: dp.units,
                  date: dp.date
                }));
              
              if (conversions.length > 0) {
                setUnitMismatches(prev => {
                  const newMap = new Map(prev);
                  newMap.set(testName, { conversions });
                  return newMap;
                });
              }
            }
            
            // Enhanced outlier detection logic
            let hasExtremeChanges = false;
            let extremeChangeDetails = '';
            const suspiciousChanges: Array<{
              index: number;
              prevIndex: number;
              ratio: number;
              validation?: any;
              type: 'extreme_change' | 'statistical_outlier' | 'zero_value';
            }> = [];
            
            // Calculate statistics for outlier detection
            const validValues = dataPoints.filter(dp => dp.value > 0).map(dp => dp.value);
            const mean = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : 0;
            const variance = validValues.length > 0 
              ? validValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / validValues.length 
              : 0;
            const stdDev = Math.sqrt(variance);
            
            // Check each data point for various types of anomalies
            for (let i = 0; i < dataPoints.length; i++) {
              const currValue = dataPoints[i].value;
              const currDate = dataPoints[i].date;
              
              // Skip if user has verified this value as correct
              const verifiedSet = verifiedValues.get(testName);
              if (verifiedSet?.has(currDate)) {
                continue; // User has marked this as correct, skip validation
              }
              
              // Flag zero or near-zero values as suspicious
              // For critical tests, any value below a certain threshold is suspicious
              const testNameLower = testName.toLowerCase();
              let isNearZero = false;
              
              if (currValue === 0) {
                // Check if zero is valid for this specific test
                if (!isZeroValidForTest(testName)) {
                  isNearZero = true;
                }
              } else if (testNameLower.includes('mchc') && currValue < 20) {
                // MCHC below 20 g/dL is physiologically impossible
                isNearZero = true;
              } else if ((testNameLower.includes('hemoglobin') || testNameLower.includes('haemoglobin')) && currValue < 5) {
                // Hemoglobin below 5 g/dL is life-threatening and likely an error
                isNearZero = true;
              }
              
              if (isNearZero) {
                suspiciousChanges.push({
                  index: i,
                  prevIndex: i - 1,
                  ratio: 0,
                  type: 'zero_value'
                });
                
                console.error(`🚨 Zero or near-zero value detected for ${testName} on ${dataPoints[i].date}: ${currValue}`);
              }
              
              // Check for extreme changes from previous value
              if (i > 0) {
                const prevValue = dataPoints[i-1].value;
                const prevDate = dataPoints[i-1].date;
                
                // Calculate time gap between measurements
                const prevDateObj = new Date(prevDate);
                const currDateObj = new Date(currDate);
                const daysBetween = Math.abs(currDateObj.getTime() - prevDateObj.getTime()) / (1000 * 60 * 60 * 24);
                
                // Only validate against previous value if measurements are within reasonable time (e.g., 1 year)
                if (daysBetween < 365) {
                  const ratio = (prevValue > 0 && currValue > 0) 
                    ? Math.max(prevValue, currValue) / Math.min(prevValue, currValue)
                    : 0;
                  
                  // Lower threshold for medical data - flag if values change by more than 2x
                  // For critical tests, use even lower threshold
                  const testNameLower = testName.toLowerCase();
                  const isCriticalTest = testNameLower.includes('mchc') || 
                                        testNameLower.includes('hemoglobin') || 
                                        testNameLower.includes('haemoglobin');
                  const threshold = isCriticalTest ? 1.5 : 2;
                  
                  if (ratio > threshold && prevValue > 0 && currValue > 0) {
                    suspiciousChanges.push({
                      index: i,
                      prevIndex: i - 1,
                      ratio,
                      type: 'extreme_change'
                    });
                    
                    console.warn(`⚠️ Extreme value change detected for ${testName}:`, {
                      previous: dataPoints[i-1],
                      current: dataPoints[i],
                      ratio: ratio.toFixed(1) + 'x',
                      dates: [dataPoints[i-1].date, dataPoints[i].date],
                      daysBetween: Math.round(daysBetween)
                    });
                  }
                }
              }
              
              // Statistical outlier detection (3 standard deviations from mean)
              if (stdDev > 0 && currValue > 0) {
                const zScore = Math.abs((currValue - mean) / stdDev);
                if (zScore > 3) {
                  // Check if not already flagged
                  const alreadyFlagged = suspiciousChanges.some(sc => sc.index === i);
                  if (!alreadyFlagged) {
                    suspiciousChanges.push({
                      index: i,
                      prevIndex: i - 1,
                      ratio: currValue / mean,
                      type: 'statistical_outlier'
                    });
                    
                    console.warn(`📊 Statistical outlier detected for ${testName}:`, {
                      value: currValue,
                      mean: mean.toFixed(2),
                      stdDev: stdDev.toFixed(2),
                      zScore: zScore.toFixed(2),
                      date: dataPoints[i].date
                    });
                  }
                }
              }
            }
            
            // Validate suspicious changes with LLM
            if (suspiciousChanges.length > 0 && user && isAuthenticated) {
              console.log(`🔍 Validating ${suspiciousChanges.length} suspicious changes for ${testName}`);
              
              // Validate each suspicious change
              for (const change of suspiciousChanges) {
                // For zero values or when there's no previous data, skip LLM validation
                if (change.type === 'zero_value' || change.prevIndex < 0) {
                  const isInvalidZero = change.type === 'zero_value' && !isZeroValidForTest(testName);
                  change.validation = {
                    isLikelyError: isInvalidZero,
                    errorType: isInvalidZero ? 'zero_value' : null,
                    severity: isInvalidZero ? 'error' : 'warning',
                    explanation: isInvalidZero 
                      ? 'Zero values are often data entry errors for this test'
                      : 'Unable to validate - no previous data for comparison'
                  };
                  if (change.type === 'zero_value') {
                    // Only flag as extreme change if zero is not valid for this test
                    if (!isZeroValidForTest(testName)) {
                      hasExtremeChanges = true;
                      extremeChangeDetails = 'Zero value detected';
                    }
                  }
                  continue;
                }
                
                const prevData = dataPoints[change.prevIndex];
                const currData = dataPoints[change.index];
                
                try {
                  const validation = await DataValidationService.validateDataChange(
                    testName,
                    prevData.value,
                    prevData.units,
                    prevData.date,
                    currData.value,
                    currData.units,
                    currData.date,
                    sessionId
                  );
                  
                  change.validation = validation;
                  
                  if (validation?.isLikelyError) {
                    hasExtremeChanges = true;
                    extremeChangeDetails = validation.explanation;
                    console.error(`🚨 LLM confirmed error for ${testName}:`, validation);
                  } else if (validation?.severity === 'warning') {
                    console.warn(`⚠️ LLM validation for ${testName}:`, validation);
                  }
                } catch (error) {
                  console.error('Failed to validate data change:', error);
                }
              }
            }
            
            // Log if conversions were made
            if (hasConversions) {
              console.log(`Unit conversions applied for ${testName}: normalized to ${targetUnit}`);
              
              // Show notification about unit conversion
              const message = `Units normalized to ${targetUnit} for ${test?.displayName || testName}`;
              setNotification({
                message,
                type: 'success'
              });
              setTimeout(() => setNotification(null), 5000);
            }
            
            // Show warning only for LLM-confirmed errors
            if (hasExtremeChanges) {
              console.error(`🚨 Data quality issue confirmed for ${testName}: ${extremeChangeDetails}`);
              
              // Find if any changes have suggested corrections
              const errorWithCorrection = suspiciousChanges.find(
                sc => sc.validation?.isLikelyError && sc.validation?.suggestedCorrection
              );
              
              // Add a warning notification
              setTimeout(() => {
                let message = `Data quality issue detected for ${test?.displayName || testName}: ${extremeChangeDetails}`;
                
                if (errorWithCorrection?.validation?.suggestedCorrection) {
                  const correction = errorWithCorrection.validation.suggestedCorrection;
                  message += ` Likely should be ${correction.value} ${correction.units}.`;
                }
                
                setNotification({
                  message,
                  type: 'warning'
                });
                setTimeout(() => setNotification(null), 10000);
              }, hasConversions ? 6000 : 0); // Delay if conversion notification is shown
            }
            
            // Build data points - backend already handles normalization
            // First, create basic data points structure preserving all properties
            const basicDataPoints = dataPoints.map((point) => ({
              ...point,
              date: point.date,
              id: point.id || 0,
              reportId: point.reportId || 0,
              value: point.value,
              units: point.units,
              originalValue: point.originalValue,
              originalUnits: point.originalUnits,
              referenceMin: point.referenceMin,
              referenceMax: point.referenceMax,
              referenceSource: point.referenceSource,
              status: (point.status?.toLowerCase() || 'unknown') as 'normal' | 'high' | 'low' | 'unknown',
              validation: point.validation // Preserve validation data from backend
            }));
            
            // Debug reference ranges
            if (testName.toLowerCase().includes('hdl') || testName.toLowerCase().includes('cholesterol')) {
              console.log(`📊 ${testName} reference ranges from API:`, {
                rangeSource: referenceRangeSource,
                firstPoint: dataPoints[0] ? {
                  referenceMin: dataPoints[0].referenceMin,
                  referenceMax: dataPoints[0].referenceMax,
                  referenceSource: dataPoints[0].referenceSource
                } : 'No data points',
                allPoints: dataPoints.map(p => ({
                  date: p.date,
                  refMin: p.referenceMin,
                  refMax: p.referenceMax
                }))
              });
            }
            
            // Run comprehensive outlier detection
            const outlierResults = detectOutliers(testName, basicDataPoints, {
              sensitivity: 'medium',
              verifiedDates: verifiedValues.get(testName)
            });
            
            // Merge outlier detection with existing suspicious detection
            const normalizedDataPoints = outlierResults.map((point, idx) => {
              let isSuspicious = false;
              let validationMessage = '';
              
              // Check if user has verified this value
              const verifiedSet = verifiedValues.get(testName);
              if (verifiedSet?.has(point.date)) {
                // User marked this as correct, don't flag as suspicious
                isSuspicious = false;
                validationMessage = '';
              } else {
                // Use outlier detection results
                if (point.isOutlier) {
                  isSuspicious = true;
                  validationMessage = point.outlierReason || 'Value appears to be an outlier';
                  
                  // Add confidence level to message if not high confidence
                  if (point.outlierConfidence === 'low') {
                    validationMessage += ' (low confidence)';
                  } else if (point.outlierConfidence === 'medium') {
                    validationMessage += ' (medium confidence)';
                  }
                } else {
                  // Check existing suspicious changes detection
                  const suspiciousChange = suspiciousChanges.find(sc => sc.index === idx);
                  
                  if (suspiciousChange) {
                    // Only mark as suspicious if validation indicates an actual error
                    if (suspiciousChange.validation) {
                      // Check if it's actually an error or just informational
                      if (suspiciousChange.validation.isLikelyError || 
                          suspiciousChange.validation.severity === 'error' ||
                          suspiciousChange.validation.severity === 'warning') {
                        isSuspicious = true;
                        validationMessage = suspiciousChange.validation.explanation || '';
                      }
                      // If severity is 'normal' or no error indicated, don't mark as suspicious
                    } else {
                      // No validation result, but suspicious change detected locally
                      isSuspicious = true;
                      validationMessage = 'Unusual value change detected';
                    }
                  }
                }
              }
              
              return {
                ...point,
                isSuspicious,
                validationMessage,
                // Use display value for extreme outliers to prevent chart scaling issues
                chartValue: point.displayValue || point.value,
                validation: point.validation // Preserve validation data
              } as TrendDataPoint;
            });
            
            // Collect all unique units used for this test
            const allUnitsForTest = new Set<string>();
            dataPoints.forEach(dp => {
              if (dp.units) allUnitsForTest.add(dp.units);
            });
            if (targetUnit) allUnitsForTest.add(targetUnit);
            
            transformedData[testName] = {
              testName,
              displayName: test?.displayName || testName,
              dataPoints: normalizedDataPoints,
              units: targetUnit,
              latestValue: normalizedDataPoints[normalizedDataPoints.length - 1]?.value,
              hasUnitConversions: hasConversions,
              availableUnits: Array.from(allUnitsForTest)
            };
            
            // Debug transformed data
            if (testName.toLowerCase().includes('hdl')) {
              console.log(`📦 Transformed HDL data:`, {
                testName,
                dataPointCount: normalizedDataPoints.length,
                firstPoint: normalizedDataPoints[0]
              });
            }
            
            // Track if this test has original reference ranges
            if (hasOriginalReference) {
              setReferenceRangeSources(prev => ({
                ...prev,
                [testName]: 'original'
              }));
            }
          }
        }));

        // Auto-correct obvious unit errors AND decimal place errors
        const unitCorrectionNotifications: string[] = [];
        const newAutoCorrectedValues = new Map<string, Array<any>>();
        
        console.log('🔍 Auto-correction check - transformedData keys:', Object.keys(transformedData));
        
        // First, let's check what raw data we received from the backend
        console.log('📊 Raw data analysis:');
        Object.entries(rawData).forEach(([testName, dataPoints]) => {
          if (testName.toLowerCase().includes('mchc') || testName.toLowerCase().includes('hemoglobin')) {
            console.log(`\n${testName} raw data from backend:`);
            if (Array.isArray(dataPoints)) {
              dataPoints.forEach(dp => {
                if (dp.date?.includes('2024-06-23') || dp.value < 10 || (dp.originalValue !== undefined && dp.originalValue < 10)) {
                  console.log(`  ${dp.date}: value=${dp.value}, originalValue=${dp.originalValue}, units=${dp.units}, originalUnits=${dp.originalUnits}`);
                }
              });
            }
          }
        });
        
        Object.entries(transformedData).forEach(([testName, trend]) => {
          // First check for decimal place errors (values off by factor of 10)
          const decimalErrors: any[] = [];
          const testNameLower = testName.toLowerCase();
          
          console.log(`\n🔍 Checking ${testName} for decimal errors...`);
          console.log(`  Test name lower: '${testNameLower}'`);
          console.log(`  Data points:`, trend.dataPoints.map(dp => ({ 
            date: dp.date, 
            value: dp.value, 
            units: dp.units,
            originalValue: dp.originalValue,
            originalUnits: dp.originalUnits,
            isSuspicious: dp.isSuspicious,
            validationMessage: dp.validationMessage
          })));
          
          // Special check for the June 23 data point that we know is 3.4
          const june23Point = trend.dataPoints.find(dp => dp.date.includes('2024-06-23'));
          if (june23Point) {
            console.log(`📍 June 23 data point for ${testName}:`, {
              ...june23Point,
              'hasOriginalValue': june23Point.originalValue !== undefined && june23Point.originalValue !== null
            });
          }
          
          trend.dataPoints.forEach((dp, index) => {
            // For auto-correction, we need to check the CURRENT value, not original
            // The current value is what's displayed and what might be wrong
            const currentValue = dp.value;
            const currentUnits = dp.units;
            const hasOriginalData = dp.originalValue !== undefined && dp.originalValue !== null;
            
            // Detailed logging for June 23 data point
            if (dp.date.includes('2024-06-23')) {
              console.log(`  🎯 JUNE 23 DATA POINT [${index}]:`, {
                currentValue,
                currentUnits,
                originalValue: dp.originalValue,
                originalUnits: dp.originalUnits,
                referenceMin: dp.referenceMin,
                referenceMax: dp.referenceMax,
                isSuspicious: dp.isSuspicious,
                'value < 10': currentValue < 10,
                'value < refMin/10': dp.referenceMin ? currentValue < dp.referenceMin / 10 : 'N/A'
              });
            }
            
            // Check if value is likely a decimal place error
            // Generic check: if value is less than 1/10th of the reference minimum, it's likely a decimal error
            if (dp.referenceMin !== null && dp.referenceMin !== undefined && dp.referenceMin > 0 &&
                dp.referenceMax !== null && dp.referenceMax !== undefined && dp.referenceMax > 0) {
              const tenthOfMin = dp.referenceMin / 10;
              if (currentValue < tenthOfMin) {
                console.log(`  🔍 Current value ${currentValue} is less than 1/10th of reference min ${dp.referenceMin}`);
                
                // Check if multiplying by 10 would put it in a reasonable range
                const correctedValue = currentValue * 10;
                if (correctedValue >= dp.referenceMin * 0.5 && correctedValue <= dp.referenceMax * 2) {
                  console.log(`  🚨 Likely decimal error: ${currentValue} → ${correctedValue}`);
                  decimalErrors.push({
                    index,
                    currentValue: currentValue,
                    currentUnits: currentUnits,
                    suggestedValue: correctedValue,
                    suggestedUnits: currentUnits,
                    confidence: 'high',
                    reason: `Value ${currentValue} ${currentUnits} appears to be missing a decimal place (normal range: ${dp.referenceMin}-${dp.referenceMax})`
                  });
                }
              }
            }
            
            // Special case: Check for MCHC mislabeling or backend over-conversion
            // For MCHC specifically, check if we have a value around 3.x that should be 33.x
            if (testNameLower.includes('mchc') && currentUnits === 'g/dL') {
              // Check if original value was mislabeled (33.5 g/L should be 33.5 g/dL)
              if (hasOriginalData && dp.originalValue !== undefined && dp.originalUnits && 
                  dp.originalUnits.toLowerCase().includes('g/l') && 
                  dp.originalValue >= 30 && dp.originalValue <= 37 &&
                  currentValue < 10) {
                console.log(`  🚨 MCHC mislabeling with backend over-conversion: ${dp.originalValue} ${dp.originalUnits} → ${currentValue} ${currentUnits}`);
                console.log(`    Original ${dp.originalValue} was mislabeled as g/L but is actually g/dL`);
                
                decimalErrors.push({
                  index,
                  currentValue: currentValue,
                  currentUnits: currentUnits,
                  suggestedValue: dp.originalValue, // Use original numerical value
                  suggestedUnits: 'g/dL',
                  confidence: 'high',
                  reason: `MCHC value ${dp.originalValue} was mislabeled as ${dp.originalUnits} but is actually g/dL`
                });
              }
              // Check for simple decimal error (3.35 should be 33.5)
              else if (currentValue < 10) {
                const correctedValue = currentValue * 10;
                if (correctedValue >= 30 && correctedValue <= 37) {
                  console.log(`  🚨 MCHC decimal error: ${currentValue} → ${correctedValue}`);
                  decimalErrors.push({
                    index,
                    currentValue: currentValue,
                    currentUnits: currentUnits,
                    suggestedValue: correctedValue,
                    suggestedUnits: currentUnits,
                    confidence: 'high',
                    reason: `MCHC value ${currentValue} g/dL is impossibly low - likely missing decimal place`
                  });
                }
              }
            }
            // Check for backend over-conversion in other tests
            else if (hasOriginalData && dp.originalValue !== undefined && dp.originalUnits && 
                dp.referenceMin !== null && dp.referenceMax !== null) {
              const originalInRange = dp.originalValue >= dp.referenceMin && dp.originalValue <= dp.referenceMax;
              const convertedTooLow = currentValue < dp.referenceMin * 0.1;
              
              if (originalInRange && convertedTooLow) {
                console.log(`  🚨 Backend over-conversion detected: ${dp.originalValue} ${dp.originalUnits} → ${currentValue} ${currentUnits}`);
                console.log(`    Original was in normal range (${dp.referenceMin}-${dp.referenceMax}), but converted value is impossibly low`);
                
                // Suggest using the original value with corrected unit
                decimalErrors.push({
                  index,
                  currentValue: currentValue, // Current wrong value
                  currentUnits: currentUnits,
                  suggestedValue: dp.originalValue, // Use original numerical value
                  suggestedUnits: currentUnits, // Keep current unit
                  confidence: 'high',
                  reason: `Backend over-converted ${dp.originalValue} ${dp.originalUnits} to ${currentValue} ${currentUnits}. The original value ${dp.originalValue} is correct.`
                });
              }
            }
            
            // Fallback for specific tests when no reference range is available
            else if (currentValue !== undefined && currentValue < 10) {
              console.log(`  🔍 Low value detected (no ref range): ${currentValue} ${currentUnits}`);
              
              // MCHC specific checks (medical knowledge: MCHC is always 30-38 g/dL range)
              if (testNameLower.includes('mchc') && currentUnits === 'g/dL' && currentValue < 10) {
                console.log(`  🚨 MCHC decimal error detected: ${currentValue} g/dL`);
                decimalErrors.push({
                  index,
                  currentValue: currentValue,
                  currentUnits: currentUnits,
                  suggestedValue: currentValue * 10,
                  suggestedUnits: currentUnits,
                  confidence: 'high',
                  reason: `MCHC value ${currentValue} ${currentUnits} is impossibly low - likely missing decimal place`
                });
              }
              // Hemoglobin specific checks (medical knowledge: Hemoglobin is typically 12-17 g/dL)
              else if ((testNameLower.includes('hemoglobin') || testNameLower.includes('haemoglobin')) && 
                       currentUnits === 'g/dL' && currentValue < 5) {
                console.log(`  🚨 Hemoglobin decimal error detected: ${currentValue} g/dL`);
                decimalErrors.push({
                  index,
                  currentValue: currentValue,
                  currentUnits: currentUnits,
                  suggestedValue: currentValue * 10,
                  suggestedUnits: currentUnits,
                  confidence: 'high',
                  reason: `Hemoglobin value ${currentValue} ${currentUnits} is impossibly low - likely missing decimal place`
                });
              }
            }
            
            // Also check if the point was marked as suspicious during initial detection
            if (dp.isSuspicious && dp.validationMessage?.toLowerCase().includes('impossibly low')) {
              console.log(`  📌 Point already flagged as suspicious: ${dp.validationMessage}`);
              // If not already added, add it to decimal errors
              const alreadyAdded = decimalErrors.some(err => err.index === index);
              if (!alreadyAdded && currentValue !== undefined && currentValue < 10) {
                // Use reference range if available
                if (dp.referenceMin !== null && dp.referenceMin !== undefined &&
                    dp.referenceMax !== null && dp.referenceMax !== undefined) {
                  const correctedValue = currentValue * 10;
                  if (correctedValue >= dp.referenceMin * 0.5 && correctedValue <= dp.referenceMax * 2) {
                    decimalErrors.push({
                      index,
                      currentValue: currentValue,
                      currentUnits: currentUnits,
                      suggestedValue: correctedValue,
                      suggestedUnits: currentUnits,
                      confidence: 'high',
                      reason: dp.validationMessage || `Value ${currentValue} is impossibly low`
                    });
                  }
                }
              }
            }
          });
          
          // Get unit errors
          const unitErrors = detectUnitErrors(testName, trend.dataPoints);
          
          // Combine decimal errors and unit errors
          const allErrors = [...decimalErrors, ...unitErrors];
          
          console.log(`🔍 ${testName}: Found ${decimalErrors.length} decimal errors, ${unitErrors.length} unit errors`);
          
          // Apply high-confidence corrections automatically
          const highConfidenceErrors = allErrors.filter(e => e.confidence === 'high');
          if (highConfidenceErrors.length > 0) {
            const testCorrections: Array<any> = [];
            
            highConfidenceErrors.forEach(error => {
              const dp = trend.dataPoints[error.index];
              // Use the ACTUAL original values if they exist (from backend normalization)
              // Otherwise use current values
              const trueOriginalValue = dp.originalValue || dp.value;
              const trueOriginalUnits = dp.originalUnits || dp.units;
              
              // Apply the correction
              trend.dataPoints[error.index] = {
                ...dp,
                value: error.suggestedValue,
                units: error.suggestedUnits,
                originalValue: trueOriginalValue,
                originalUnits: trueOriginalUnits,
                autoFixed: true
              };
              
              // Track the correction with true original values
              testCorrections.push({
                date: dp.date,
                originalValue: trueOriginalValue,
                originalUnits: trueOriginalUnits,
                correctedValue: error.suggestedValue,
                correctedUnits: error.suggestedUnits,
                reason: error.reason
              });
              
              // Format notification based on whether value changed
              if (trueOriginalValue === error.suggestedValue) {
                unitCorrectionNotifications.push(
                  `${testName}: Corrected unit label from ${trueOriginalUnits} to ${error.suggestedUnits}`
                );
              } else {
                unitCorrectionNotifications.push(
                  `${testName}: Converted ${trueOriginalValue} ${trueOriginalUnits} → ${error.suggestedValue.toFixed(1)} ${error.suggestedUnits}`
                );
              }
            });
            
            if (testCorrections.length > 0) {
              newAutoCorrectedValues.set(testName, testCorrections);
            }
          }
        });
        
        setAutoCorrectedValues(newAutoCorrectedValues);

        // If this is an incremental load, merge with existing data
        if (testsToLoad) {
          setTrendsData(prevData => {
            const newData = {
              ...prevData,
              ...transformedData
            };
            console.log('💾 Setting trends data (incremental):', {
              previousKeys: Object.keys(prevData),
              newKeys: Object.keys(transformedData),
              finalKeys: Object.keys(newData)
            });
            return newData;
          });
        } else {
          // Full reload - replace all data
          console.log('💾 Setting trends data (full):', {
            keys: Object.keys(transformedData),
            data: transformedData
          });
          setTrendsData(transformedData);
        }
        
        // Show unit correction notifications
        if (unitCorrectionNotifications.length > 0) {
          setNotification({
            message: `Auto-corrected ${unitCorrectionNotifications.length} unit error${unitCorrectionNotifications.length > 1 ? 's' : ''}: ${unitCorrectionNotifications.join(', ')}`,
            type: 'success'
          });
          setTimeout(() => setNotification(null), 8000);
        }
        
        // Outlier detection is now handled by auto-correction and visual indicators
        
        // Debug reference ranges
        debugReferenceRanges(transformedData);
        
        // Log data summary
        if (Object.keys(transformedData).length === 0) {
          console.log('No trend data found for selected tests');
        } else {
          console.log('Trends data loaded:', {
            tests: Object.keys(transformedData),
            dataPointCounts: Object.entries(transformedData).reduce((acc, [test, trend]) => {
              acc[test] = trend.dataPoints.length;
              return acc;
            }, {} as Record<string, number>),
            dateRanges: Object.entries(transformedData).reduce((acc, [test, trend]) => {
              if (trend.dataPoints.length > 0) {
                const dates = trend.dataPoints.map(dp => new Date(dp.date));
                const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
                const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
                acc[test] = {
                  oldest: minDate.toLocaleDateString(),
                  newest: maxDate.toLocaleDateString()
                };
              }
              return acc;
            }, {} as Record<string, { oldest: string; newest: string }>)
          });
        }
      }
    } catch (error: any) {
      console.error('Error loading trends:', error);
      
      // Don't show error if it's a 401 - let the session handler deal with it
      if (error.response?.status === 401) {
        // Session handler will retry the request
        return;
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to load trends data');
      }
    } finally {
      // Clear loading state for these tests
      testsToFetch.forEach(test => loadingTestsRef.current.delete(test));
      setLoadingTests(new Set(loadingTestsRef.current));
      
      // Update global cache with successfully fetched data
      testsToFetch.forEach(test => {
        if (transformedData[test]) {
          const cacheKey = dateRange.preset || `custom_${dateRange.startDate}_${dateRange.endDate}`;
          setCachedTrend(test, transformedData[test], cacheKey, referenceRangeSource);
        }
      });
      
      // Only clear loading state if this was a full load
      if (!testsToLoad) {
        setLoading(false);
      }
    }
  }, [user, isAuthenticated, selectedTests, dateRange, availableTests, referenceRangeSource]);

  // Reset component state when user changes
  useEffect(() => {
    if (user) {
      console.log('User changed, resetting TrendsView state for:', user.email);
      // Clear all component state
      setSelectedTests([]);
      setTrendsData({});
      setAvailableTests([]);
      setLoading(false);
      setError(null);
      setLoadingTests(new Set());
      setEditingPoint(null);
      setNotification(null);
      setReferenceRangeSources({});
      setReferenceRangeDetails({});
      
      // Clear any cached data for previous user
      TrendsCache.clearAll();
    }
  }, [user?.id]); // Only reset when user ID changes

  // Load available tests on mount
  useEffect(() => {
    let mounted = true;
    
    // Don't clear caches on mount - preserve cached data
    resetLoadCounter(); // Reset debug counter on mount
    console.log('TrendsView mounted, auth state:', { 
      hasUser: !!user, 
      userEmail: user?.email,
      hasToken: !!isAuthenticated 
    });
    
    if (mounted && user && isAuthenticated) {
      debugDataLoading('useEffect - loadAvailableTests', {
        trigger: 'mount/auth change',
        userEmail: user.email
      });
      loadAvailableTests();
    }
    
    return () => {
      mounted = false;
    };
  }, [loadAvailableTests, user, isAuthenticated]);
  
  // Optimize initial load with cache check
  useEffect(() => {
    if (selectedTests.length > 0 && Object.keys(trendsData).length === 0) {
      // Check cache first before loading
      const cachedResults: Record<string, TestTrend> = {};
      const uncachedTests: string[] = [];
      
      selectedTests.forEach(test => {
        const cacheKey = dateRange.preset || `custom_${dateRange.startDate}_${dateRange.endDate}`;
        const cached = getCachedTrend(test, cacheKey, referenceRangeSource);
        if (cached) {
          cachedResults[test] = cached;
        } else {
          uncachedTests.push(test);
        }
      });
      
      // Update with cached data immediately
      if (Object.keys(cachedResults).length > 0) {
        console.log(`🚀 Loaded ${Object.keys(cachedResults).length} tests from cache instantly`);
        setTrendsData(prev => ({ ...prev, ...cachedResults }));
      }
      
      // Fetch only uncached tests
      if (uncachedTests.length > 0) {
        loadTrendsData(uncachedTests);
      }
    }
  }, []); // Run only on mount
  
  // Set selected date when data changes
  useEffect(() => {
    if (availableDates.length > 0 && !selectedSingleDate) {
      setSelectedSingleDate(availableDates[0]); // Select most recent date
    }
  }, [availableDates, selectedSingleDate]);

  // Load trends when tests, date range, or reference range source changes
  useEffect(() => {
    const previousTests = previousSelectedTestsRef.current;
    const previousDateRange = previousDateRangeRef.current;
    const previousReferenceSource = previousReferenceSourceRef.current;
    
    // Check what changed
    const dateRangeChanged = previousDateRange.preset !== dateRange.preset || 
                           previousDateRange.startDate !== dateRange.startDate ||
                           previousDateRange.endDate !== dateRange.endDate;
    const referenceSourceChanged = previousReferenceSource !== referenceRangeSource;
    const testsChanged = JSON.stringify(previousTests) !== JSON.stringify(selectedTests);
    
    // Check if only selectedTests changed (not date range or reference source)
    if (previousTests.length > 0 || selectedTests.length > 0) {
      // Find which tests were added or removed
      const addedTests = selectedTests.filter(test => !previousTests.includes(test));
      const removedTests = previousTests.filter(test => !selectedTests.includes(test));
      
      debugDataLoading('useEffect - loadTrendsData', {
        trigger: 'selectedTests/dateRange/referenceRangeSource change',
        selectedTests,
        previousTests,
        addedTests,
        removedTests,
        dateRange: dateRange.label,
        referenceRangeSource,
        dateRangeChanged,
        referenceSourceChanged,
        testsChanged
      });
      
      // If only tests changed (not date range or reference source)
      if (testsChanged && !dateRangeChanged && !referenceSourceChanged) {
        if (addedTests.length > 0 && removedTests.length === 0) {
          // Incremental load - just load the new tests
          loadTrendsData(addedTests);
        } else if (removedTests.length > 0 && addedTests.length === 0) {
          // Just remove tests from the UI
          setTrendsData(prevData => {
            const newData = { ...prevData };
            removedTests.forEach(test => {
              delete newData[test];
            });
            return newData;
          });
          // Also remove from auto-corrected values
          setAutoCorrectedValues(prev => {
            const newMap = new Map(prev);
            removedTests.forEach(test => {
              newMap.delete(test);
            });
            return newMap;
          });
          // Remove from loading reference ranges
          setLoadingReferenceRanges(prev => {
            const newSet = new Set(prev);
            removedTests.forEach(test => {
              newSet.delete(test);
            });
            return newSet;
          });
        } else if (selectedTests.length > 0) {
          // Complex test changes (both add and remove) - full reload
          loadTrendsData();
        }
      } else if (selectedTests.length > 0 && (dateRangeChanged || referenceSourceChanged)) {
        // Full reload needed (date range or reference source changed)
        loadTrendsData();
      }
    }
    
    // Update the refs for next comparison
    previousSelectedTestsRef.current = selectedTests;
    previousDateRangeRef.current = dateRange;
    previousReferenceSourceRef.current = referenceRangeSource;
  }, [selectedTests, dateRange, referenceRangeSource, loadTrendsData]);

  // Set up global function for review button
  useEffect(() => {
    (window as any).reviewAutoCorrections = (testName?: string) => {
      setCorrectionManagerTestFilter(testName || null);
      setShowCorrectionManager(true);
    };
    
    return () => {
      delete (window as any).reviewAutoCorrections;
    };
  }, []);

  // Initialize selectedSingleDate when view mode changes
  useEffect(() => {
    if (effectiveViewMode === 'single' && !selectedSingleDate) {
      if (availableDates.length > 0) {
        setSelectedSingleDate(availableDates[0]); // Select most recent date
      }
    }
  }, [effectiveViewMode, selectedSingleDate, availableDates]);

  // Automatically fetch missing reference ranges with improved throttling
  useEffect(() => {
    const INITIAL_DELAY = 2000; // Start with 2 seconds
    const MAX_DELAY = 30000; // Max 30 seconds
    const BATCH_SIZE = 3; // Process 3 tests at a time
    
    const fetchMissingReferenceRanges = async () => {
      if (!user || !isAuthenticated) {
        console.log('No auth, skipping reference range fetch');
        return;
      }
      
      console.log('Checking for tests without reference ranges...', {
        trendsDataKeys: Object.keys(trendsData),
        loadingReferenceRanges: Array.from(loadingReferenceRanges),
        referenceRangeSources
      });
      
      // Find tests without reference ranges
      const testsWithoutRanges = Object.entries(trendsData)
        .filter(([testName, trend]) => {
          const hasData = trend.dataPoints.length > 0;
          const hasReference = trend.dataPoints.some(
            point => point.referenceMin !== null && point.referenceMax !== null
          );
          const isAlreadyLoading = loadingReferenceRanges.has(testName);
          const alreadyEnhanced = referenceRangeSources[testName] === 'enhanced';
          
          console.log(`Test ${testName}:`, {
            hasData,
            hasReference,
            isAlreadyLoading,
            alreadyEnhanced,
            shouldFetch: hasData && !hasReference && !isAlreadyLoading && !alreadyEnhanced
          });
          
          return hasData && !hasReference && !isAlreadyLoading && !alreadyEnhanced;
        })
        .map(([testName]) => testName);
      
      console.log('Tests that need reference ranges:', testsWithoutRanges);
      
      // Process in batches with exponential backoff
      let currentDelay = INITIAL_DELAY;
      let consecutiveErrors = 0;
      
      // TEST MODE: Log throttling behavior
      console.log('🔄 THROTTLING TEST - Starting reference range fetch', {
        totalTests: testsWithoutRanges.length,
        batchSize: BATCH_SIZE,
        initialDelay: INITIAL_DELAY,
        maxDelay: MAX_DELAY
      });
      
      for (let i = 0; i < testsWithoutRanges.length; i += BATCH_SIZE) {
        const batch = testsWithoutRanges.slice(i, i + BATCH_SIZE);
        console.log(`📦 Processing batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(testsWithoutRanges.length / BATCH_SIZE)}:`, batch);
        
        // Process batch with limited concurrency
        // eslint-disable-next-line no-loop-func
        const batchPromises = batch.map(async (testName, index) => {
          // Stagger requests within the batch
          const staggerDelay = index * 500;
          console.log(`⏱️ Request ${index + 1}/${batch.length} for ${testName} - waiting ${staggerDelay}ms`);
          await new Promise(resolve => setTimeout(resolve, staggerDelay));
          
          const startTime = Date.now();
          try {
            await handleFetchReferenceRange(testName);
            const duration = Date.now() - startTime;
            console.log(`✅ Success: ${testName} (took ${duration}ms)`);
            consecutiveErrors = 0; // Reset on success
            return { testName, success: true, duration };
          } catch (error: any) {
            const duration = Date.now() - startTime;
            console.error(`❌ Failed: ${testName} (took ${duration}ms):`, error);
            
            // Don't count 500 errors as consecutive errors (server issues, not rate limiting)
            if (error?.response?.status !== 500) {
              consecutiveErrors++;
            } else {
              console.log(`ℹ️ Server error for ${testName}, not counting as rate limit`);
            }
            
            return { testName, success: false, error, duration };
          }
        });
        
        // Wait for batch to complete
        const results = await Promise.all(batchPromises);
        const failedCount = results.filter(r => !r.success).length;
        const avgDuration = results.reduce((sum, r) => sum + (r.duration || 0), 0) / results.length;
        
        console.log(`📊 Batch results: ${results.length - failedCount}/${results.length} successful (avg ${Math.round(avgDuration)}ms)`);
        
        // If we have failures, increase delay exponentially
        if (failedCount > 0) {
          const oldDelay = currentDelay;
          currentDelay = Math.min(currentDelay * 2, MAX_DELAY);
          console.log(`🔺 ${failedCount} requests failed, increasing delay: ${oldDelay}ms → ${currentDelay}ms`);
        } else if (currentDelay > INITIAL_DELAY) {
          // Gradually reduce delay on success
          const oldDelay = currentDelay;
          currentDelay = Math.max(currentDelay * 0.8, INITIAL_DELAY);
          console.log(`🔻 All successful, reducing delay: ${oldDelay}ms → ${currentDelay}ms`);
        }
        
        // Stop if we're getting too many consecutive errors (likely rate limited)
        if (consecutiveErrors >= 3) {
          console.warn('Too many consecutive errors, stopping reference range fetching');
          setNotification({
            message: 'Some reference ranges could not be fetched. Please try again later.',
            type: 'warning'
          });
          setTimeout(() => setNotification(null), 5000);
          break;
        }
        
        // Wait before next batch
        if (i + BATCH_SIZE < testsWithoutRanges.length) {
          console.log(`Waiting ${currentDelay}ms before next batch...`);
          // eslint-disable-next-line no-loop-func
          await new Promise(resolve => setTimeout(resolve, currentDelay));
        }
      }
    };
    
    // Only run after trends data has been loaded
    if (Object.keys(trendsData).length > 0) {
      console.log('Trends data loaded, checking for missing reference ranges...');
      // Add a delay to ensure the component has fully rendered
      // TODO: Implement /api/reference-range/fetch endpoint in backend
      // setTimeout(() => {
      //   fetchMissingReferenceRanges();
      // }, 1000);
    } else {
      console.log('No trends data yet, skipping reference range check');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendsData, user, isAuthenticated, userDemographics, loadingReferenceRanges, referenceRangeSources]);

  // Handle data point clicks
  // Calculate status based on numeric reference range
  const calculateStatus = (value: number, refMin: number | null, refMax: number | null): 'normal' | 'high' | 'low' | 'unknown' => {
    if (refMin === null || refMax === null) {
      return 'unknown';
    }
    if (value < refMin) {
      return 'low';
    } else if (value > refMax) {
      return 'high';
    }
    return 'normal';
  };

  // Handle getting LLM explanation for a data point
  const handleGetLLMExplanation = async (marker: string, value: number, units: string) => {
    console.log('Getting LLM explanation for:', marker, value, units);
    
    setSelectedMarkerForExplanation(marker);
    setLlmExplanation(null);
    setIsLoadingExplanation(true);
    setExplanationError(null);
    setExplanationValue(value);
    setExplanationUnits(units);
    
    try {
      const response = await apiService.post('/api/llm_explanation', {
        marker,
        value,
        units
      });
      
      if (response.status === 'success' && response.data?.explanation) {
        setLlmExplanation(response.data.explanation);
      } else {
        setExplanationError('Unable to get explanation for this lab value.');
      }
    } catch (error: any) {
      console.error('LLM explanation error:', error);
      
      let errorMessage = 'Failed to get explanation for this lab value.';
      if (error.response?.status === 400) {
        errorMessage = 'This lab value is not available for explanation.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setExplanationError(errorMessage);
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  const handleDataPointClick = (event: DataPointClickEvent, position: { x: number; y: number }) => {
    // Try to find the trend - first exact match, then case-insensitive
    let trend = trendsData[event.testName];
    let actualTestName = event.testName;
    
    if (!trend) {
      // Try case-insensitive search
      const testNameLower = event.testName.toLowerCase();
      const match = Object.keys(trendsData).find(key => key.toLowerCase() === testNameLower);
      if (match) {
        trend = trendsData[match];
        actualTestName = match;
      }
    }
    
    if (!trend) {
      return;
    }
    
    const dataPoint = trend.dataPoints[event.dataIndex];
    if (!dataPoint) {
      return;
    }
    
    // Check if this is a suspicious value that user might want to mark as correct
    const isSuspicious = !!(dataPoint.isSuspicious && dataPoint.validationMessage);
    
    const editingData = {
      testName: actualTestName,  // Use the actual test name from trendsData
      dataIndex: event.dataIndex,
      value: event.value,
      date: event.date,
      units: event.units,
      originalValue: dataPoint.originalValue,
      originalUnits: dataPoint.originalUnits,
      availableUnits: trend.availableUnits,
      position: {
        x: position.x,
        y: position.y
      },
      referenceRange: {
        min: event.referenceMin,
        max: event.referenceMax
      },
      reportId: dataPoint.reportId,
      resultId: dataPoint.id,
      isSuspicious,
      validationMessage: dataPoint.validationMessage
    };
    
    setEditingPoint(editingData);
  };

  // Handle dismissing validation (keeping original value but clearing warning)
  const handleDismissValidation = (testName: string, dataIndex: number) => {
    const trend = trendsData[testName];
    if (!trend) return;
    
    // Update the data point to clear validation warnings but keep original value
    const updatedDataPoints = trend.dataPoints.map((dp, idx) => 
      idx === dataIndex 
        ? {
            ...dp,
            // Clear ALL validation-related flags but keep the value
            isSuspicious: false,
            validation: undefined,
            validationMessage: undefined,
            isOutlier: false,
            outlierConfidence: undefined
          }
        : dp
    );
    
    // Update trends data
    setTrendsData(prevTrends => ({
      ...prevTrends,
      [testName]: {
        ...trend,
        dataPoints: updatedDataPoints
      }
    }));
  };

  // Handle accepting validation suggestion directly
  const handleAcceptValidation = async (testName: string, dataIndex: number, suggestedValue: number, suggestedUnit: string) => {
    if (!user || !isAuthenticated) return;
    
    const trend = trendsData[testName];
    if (!trend) return;
    
    const dataPoint = trend.dataPoints[dataIndex];
    if (!dataPoint?.id) return;
    
    try {
      // Round the suggested value
      const roundedValue = roundValue(suggestedValue, testName);
      
      // Call API to update the value
      const response = await apiService.updateLabResult(dataPoint.id, {
        value: roundedValue,
        units: suggestedUnit
      });
      
      if (response.status === 'success') {
        // Update the data point in the trend
        const updatedDataPoints = trend.dataPoints.map((dp, idx) => 
          idx === dataIndex 
            ? {
                ...dp,
                value: roundedValue,
                units: suggestedUnit,
                status: calculateStatus(roundedValue, dp.referenceMin, dp.referenceMax),
                // Clear ALL validation-related flags
                isSuspicious: false,
                validation: undefined,
                validationMessage: undefined,
                isOutlier: false,
                outlierConfidence: undefined
              }
            : dp
        );
        
        // Update trends data
        setTrendsData(prevTrends => {
          const newTrends = {
            ...prevTrends,
            [testName]: {
              ...trend,
              dataPoints: updatedDataPoints,
              latestValue: updatedDataPoints[updatedDataPoints.length - 1]?.value
            }
          };
          
          // Debug logging
          console.log('After accepting validation:', {
            testName,
            dataIndex,
            oldValidation: trend.dataPoints[dataIndex].validation,
            newValidation: newTrends[testName].dataPoints[dataIndex].validation,
            updatedDataPoint: newTrends[testName].dataPoints[dataIndex]
          });
          
          return newTrends;
        });
        
        // Show success notification
        setNotification({
          message: 'Validation suggestion accepted and value updated',
          type: 'success'
        });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (error) {
      console.error('Error accepting validation:', error);
      setNotification({
        message: 'Failed to update value',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Handle saving edited value
  const handleSaveEdit = async (newValue: number, newUnit?: string) => {
    if (!editingPoint || !user || !isAuthenticated) return;
    
    // Round the new value to appropriate decimal places
    const roundedValue = roundValue(newValue, editingPoint.testName);
    
    console.log('Saving edit:', {
      testName: editingPoint.testName,
      dataIndex: editingPoint.dataIndex,
      oldValue: editingPoint.value,
      newValue: roundedValue,
      originalInput: newValue,
      resultId: editingPoint.resultId,
      oldUnit: editingPoint.units,
      newUnit: newUnit || editingPoint.units,
      unitChanged: newUnit && newUnit !== editingPoint.units
    });
    
    try {
      const trend = trendsData[editingPoint.testName];
      if (!trend) {
        console.error('Trend not found for test:', editingPoint.testName);
        return;
      }
      
      // Find the actual data point in the sorted array
      const dataPoints = [...trend.dataPoints].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const dataPoint = dataPoints[editingPoint.dataIndex];
      
      if (!dataPoint) {
        console.error('Data point not found at index:', editingPoint.dataIndex);
        return;
      }
      
      // Log the dataPoint to see its structure
      console.log('DataPoint structure:', dataPoint);
      console.log('Using result ID:', dataPoint.id);
      
      // Call API to update the value
      const response = await apiService.updateLabResult(dataPoint.id, {
        value: roundedValue,
        units: newUnit || editingPoint.units
      });
      
      console.log('API response:', response.data);
      
      if (response.status === 'success') {
        // Update the data point in the trend
        const updatedDataPoints = trend.dataPoints.map(dp => 
          dp.id === dataPoint.id 
            ? {
                ...dp,
                value: roundedValue,
                units: newUnit || dp.units,
                status: calculateStatus(roundedValue, dp.referenceMin, dp.referenceMax),
                // Clear ALL validation flags since user has verified
                isSuspicious: false,
                validation: undefined,
                validationMessage: '',
                isOutlier: false,
                outlierConfidence: undefined
              }
            : dp
        );
        
        console.log('Updated data points:', {
          testName: editingPoint.testName,
          oldDataPoints: trend.dataPoints.map(dp => ({ id: dp.id, value: dp.value })),
          newDataPoints: updatedDataPoints.map(dp => ({ id: dp.id, value: dp.value }))
        });
        
        // Update local state - create a completely new object to ensure React detects the change
        setTrendsData(prevTrends => {
          const newTrends = { ...prevTrends };
          newTrends[editingPoint.testName] = {
            ...trend,
            dataPoints: updatedDataPoints,
            // Recalculate latest value if this was the latest
            latestValue: updatedDataPoints[updatedDataPoints.length - 1]?.value
          };
          
          console.log('Setting new trends data:', newTrends);
          return newTrends;
        });
        
        // Mark this value as verified so it won't be flagged as suspicious
        const newVerifiedValues = new Map(verifiedValues);
        const testSet = newVerifiedValues.get(editingPoint.testName) || new Set();
        testSet.add(editingPoint.date);
        newVerifiedValues.set(editingPoint.testName, testSet);
        setVerifiedValues(newVerifiedValues);
        
        // Show success notification
        const unitChanged = newUnit && newUnit !== editingPoint.units;
        setNotification({
          message: unitChanged 
            ? `Value updated and converted to ${newUnit}` 
            : 'Value updated successfully',
          type: 'success'
        });
        
        // Hide notification after 3 seconds
        setTimeout(() => setNotification(null), 3000);
        
        // Force a complete reload of the specific test to ensure chart updates
        setTimeout(async () => {
          console.log('Refreshing trend data after edit...');
          
          // Clear cache by reloading - this will fetch fresh data
          await loadTrendsData([editingPoint.testName]);
        }, 100);
      } else {
        console.error('API returned non-success status:', response.data);
        setNotification({
          message: response.data.message || 'Failed to update value',
          type: 'error'
        });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (error: any) {
      console.error('Error updating value:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setNotification({
        message: 'Failed to update value',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setEditingPoint(null);
    }
  };

  // Handle fetching reference range from LLM
  const handleFetchReferenceRange = useCallback(async (testName: string) => {
    if (!user || !isAuthenticated) return;
    
    console.log(`handleFetchReferenceRange called for ${testName}`);
    
    // Mark as loading
    setLoadingReferenceRanges(prev => new Set(Array.from(prev).concat(testName)));
    
    try {
      const trend = trendsData[testName];
      if (!trend || trend.dataPoints.length === 0) {
        console.log(`No trend data found for ${testName}`);
        return;
      }
      
      // Use the units from the first data point
      const units = trend.units;
      console.log(`Fetching reference range for ${testName} with units ${units}`);
      
      const result = await ReferenceRangeService.getCachedOrFetch(
        testName,
        units,
        sessionId,
        userDemographics
      );
      
      console.log(`Reference range fetch result for ${testName}:`, result);
      
      if (result && result.referenceMin !== null && result.referenceMax !== null) {
        // Update the trends data with the fetched reference range
        const updatedTrends = { ...trendsData };
        const trendCopy = { ...updatedTrends[testName] };
        
        // Check if we need to convert the reference range units
        let refMin = result.referenceMin;
        let refMax = result.referenceMax;
        
        // If the fetched units don't match the trend units, convert
        if (result.units && result.units !== trend.units) {
          console.log(`Converting reference range from ${result.units} to ${trend.units}`);
          const minConv = convertValue(result.referenceMin, result.units, trend.units, testName);
          const maxConv = convertValue(result.referenceMax, result.units, trend.units, testName);
          
          if (minConv.converted) {
            refMin = minConv.value;
            console.log(`Converted min: ${result.referenceMin} ${result.units} → ${refMin} ${trend.units}`);
          }
          if (maxConv.converted) {
            refMax = maxConv.value;
            console.log(`Converted max: ${result.referenceMax} ${result.units} → ${refMax} ${trend.units}`);
          }
        }
        
        // Update all data points with the new reference range
        trendCopy.dataPoints = trendCopy.dataPoints.map(point => ({
          ...point,
          referenceMin: refMin,
          referenceMax: refMax,
          status: calculateStatus(point.value, refMin, refMax)
        }));
        
        updatedTrends[testName] = trendCopy;
        setTrendsData(updatedTrends);
        
        // Mark as enhanced source
        setReferenceRangeSources(prev => ({
          ...prev,
          [testName]: 'enhanced'
        }));
        
        // Store source details
        setReferenceRangeDetails(prev => ({
          ...prev,
          [testName]: {
            source: result.source || (result.cached ? 'Cached medical reference' : 'AI-researched'),
            notes: result.notes || (result.cached ? 'From database cache' : 'Fetched via OpenAI')
          }
        }));
        
        // Show notification
        const notificationMessage = result.cached 
          ? `Cached reference range loaded for ${trend.displayName || testName}` 
          : `AI-researched reference range added for ${trend.displayName || testName}`;
        setNotification({
          message: notificationMessage,
          type: 'success'
        });
        setTimeout(() => setNotification(null), 5000);
      }
    } catch (error) {
      console.error('Error fetching reference range:', error);
      setNotification({
        message: 'Failed to fetch reference range',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      // Remove from loading
      setLoadingReferenceRanges(prev => {
        const newSet = new Set(prev);
        newSet.delete(testName);
        return newSet;
      });
    }
  }, [user, isAuthenticated, trendsData, userDemographics]);

  // Clear selected tests from saved preferences
  const handleClearSaved = useCallback(() => {
    localStorage.removeItem('trendsView_selectedTests');
    setSelectedTests([]);
    setNotification({
      message: 'Saved test selection cleared',
      type: 'success'
    });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const prepareChartData = (trend: TestTrend) => {
    const sortedData = [...trend.dataPoints].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Get reference ranges from the first data point that has them
    const refPoint = sortedData.find(dp => 
      dp.referenceMin !== null && dp.referenceMax !== null
    );
    
    // Get the actual reference source from the data point
    const actualReferenceSource = refPoint?.referenceSource || 'none';
    
    // Debug: Log what's in the sorted data
    if (trend.testName.toLowerCase().includes('hdl') || trend.testName.toLowerCase().includes('cholesterol')) {
      console.log(`📈 Preparing chart for ${trend.testName}:`, {
        dataPointCount: sortedData.length,
        firstDataPoint: sortedData[0],
        refPoint: refPoint || 'NOT FOUND',
        referenceRangeSource,
        actualReferenceSource
      });
    }
    
    // Check if we're using lab ranges but none are available
    const usingLabRangesButNoneAvailable = referenceRangeSource === 'lab' && !refPoint;
    
    // Check if units are valid for this test type
    const unitValidation = detectLikelyUnitError(trend.testName, sortedData[0]?.value || 0, trend.units);
    
    // Debug reference ranges and unit issues
    if (!refPoint && unitValidation.isLikelyError) {
      console.warn(`⚠️ No reference range for ${trend.displayName || trend.testName} - likely due to invalid unit:`, {
        currentUnit: trend.units,
        suggestedUnit: unitValidation.suggestedUnit,
        reason: unitValidation.reason
      });
    }
    
    // Debug reference ranges
    if (trend.testName.toLowerCase().includes('hdl') || trend.testName.toLowerCase().includes('cholesterol')) {
      console.log(`🔍 ${trend.testName} Reference Range Debug:`, {
        testName: trend.testName,
        referenceRangeSource: referenceRangeSource,
        allDataPoints: sortedData.map(dp => ({
          date: dp.date,
          value: dp.value,
          referenceMin: dp.referenceMin,
          referenceMax: dp.referenceMax,
          units: dp.units
        })),
        refPoint: refPoint ? {
          min: refPoint.referenceMin,
          max: refPoint.referenceMax,
          units: refPoint.units
        } : 'No reference point found'
      });
    }
    
    // Debug reference ranges
    if (refPoint && (trend.testName.toLowerCase().includes('mchc') || trend.testName.toLowerCase().includes('hemoglobin'))) {
      console.log(`Reference range for ${trend.displayName || trend.testName}:`, {
        min: refPoint.referenceMin,
        max: refPoint.referenceMax,
        units: trend.units,
        dataPointUnits: refPoint.units,
        originalUnits: refPoint.originalUnits
      });
    }

    return {
      labels: sortedData.map(d => new Date(d.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: '2-digit'
      })),
      values: sortedData.map(d => Number(d.value.toFixed(6))), // Round to 6 decimal places to avoid floating point issues
      displayValues: sortedData.map(d => {
        // Use chartValue if available (capped for extreme outliers)
        const displayVal = (d as any).chartValue || d.value;
        return Number(displayVal.toFixed(6));
      }),
      referenceMin: refPoint?.referenceMin ?? undefined,
      referenceMax: refPoint?.referenceMax ?? undefined,
      statuses: sortedData.map(d => d.status || 'unknown') as ('normal' | 'high' | 'low' | 'unknown')[],
      outlierConfidences: sortedData.map(d => (d as any).outlierConfidence),
      reportIds: sortedData.map(d => d.reportId),
      suspiciousFlags: sortedData.map(d => d.isSuspicious || false),
      validationMessages: sortedData.map(d => d.validationMessage || ''),
      resultIds: sortedData.map(d => d.id),
      unitError: unitValidation.isLikelyError ? unitValidation : undefined,
      referenceSource: actualReferenceSource as 'original' | 'enhanced' | 'none' | 'standard' | 'lab',
      hasUnitConversions: trend.hasUnitConversions,
      originalValues: sortedData.map(d => d.originalValue || d.value),
      originalUnits: sortedData.map(d => d.originalUnits || d.units)
    };
  };

  // Clear editing point on mount to allow validation warnings to show
  useEffect(() => {
    setEditingPoint(null);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Health Trends Dashboard</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // Clear reference range cache
                  ReferenceRangeService.clearCache();
                  // Reset state
                  setTrendsData({});
                  setLoadingReferenceRanges(new Set());
                  setReferenceRangeSources({});
                  setReferenceRangeDetails({});
                  setUnitMismatches(new Map());
                  // Reload data
                  loadAvailableTests();
                  if (selectedTests.length > 0) {
                    loadTrendsData();
                  }
                  // Show notification
                  setNotification({
                    message: 'Data refreshed successfully',
                    type: 'success'
                  });
                  setTimeout(() => setNotification(null), 3000);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
                  title="Back to Dashboard"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="hidden sm:inline">Back</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Controls */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* User Demographics */}
              <UserDemographics 
                onUpdate={setUserDemographics}
                initialData={userDemographics}
              />
              
              {/* Reference Range Toggle */}
              <ReferenceRangeToggle
                source={referenceRangeSource}
                onChange={setReferenceRangeSource}
                hasStandardRanges={selectedTests.length > 0}
                showReferenceRanges={showReferenceRanges}
                onShowReferenceRangesChange={setShowReferenceRanges}
              />
              
              {/* Date Range Selector */}
              <DateRangeSelector
                value={dateRange}
                onChange={setDateRange}
              />

              {/* Test Selector */}
              <TestSelector
                availableTests={availableTests}
                selectedTests={selectedTests}
                onChange={setSelectedTests}
                onClearSaved={handleClearSaved}
              />
            </div>
          </div>

          {/* Main Area - Charts */}
          <div className="lg:col-span-3">
            {/* Removed Outlier Alert - now handled by auto-correction and visual indicators */}
            
            {/* Lab-Level Unit Issues Banner - Only show if there are systematic issues */}
            {showLabIssuesBanner && labAnalysisResult && labAnalysisResult.systematicIssues.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-amber-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="text-sm text-amber-800 font-medium">
                        Lab reporting issue: {labAnalysisResult.systematicIssues.length} test{labAnalysisResult.systematicIssues.length > 1 ? 's' : ''} affected
                      </span>
                      <span className="text-xs text-amber-700 ml-2">
                        Values have been auto-corrected
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowLabIssuesBanner(false)}
                    className="text-amber-600 hover:text-amber-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading trends...</p>
                </div>
              </div>
            )}

            {!loading && selectedTests.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No tests selected</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Select one or more tests from the sidebar to view trends
                </p>
              </div>
            )}

            {!loading && selectedTests.length > 0 && (
              <div className="space-y-8">
                {/* View Mode Toggle */}
                {(() => {
                  const effectiveMode = effectiveViewMode;
                  const availableDatesArray = availableDates;
                  const canToggleView = availableDatesArray.length > 1 && availableDatesArray.length <= 10;
                  
                  return canToggleView ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-700">View Mode</h3>
                          <p className="text-xs text-gray-500 mt-1">
                            You have {availableDatesArray.length} test dates • Switch between viewing modes
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setViewMode('single')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                              effectiveMode === 'single'
                                ? 'bg-blue-50 text-blue-700 border-2 border-blue-500'
                                : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>By Date</span>
                          </button>
                          <button
                            onClick={() => setViewMode('trends')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                              effectiveMode === 'trends'
                                ? 'bg-blue-50 text-blue-700 border-2 border-blue-500'
                                : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <span>Trends</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Render based on effective view mode */}
                {(() => {
                  const effectiveMode = effectiveViewMode;
                  const availableDatesArray = availableDates;
                  
                  if (effectiveMode === 'single') {
                    // Single Date View
                    return (
                      <>
                        {/* Date Navigation */}
                        {availableDatesArray.length > 1 && selectedSingleDate && (
                          <DateSelector
                            dates={availableDatesArray}
                            selectedDate={selectedSingleDate}
                            onDateSelect={setSelectedSingleDate}
                          />
                        )}
                        
                        {/* Header with info */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                          <div className="text-center">
                            <p className="text-sm text-blue-700">Reference ranges shown are based on standard medical values</p>
                            <p className="text-xs text-blue-600 mt-1">
                              <span className="inline-flex items-center gap-2">
                                <span className="w-3 h-3 bg-green-400 rounded"></span> Normal Range
                                <span className="mx-2">|</span>
                                <span className="w-3 h-3 bg-red-300 rounded"></span> Outside Normal Range
                              </span>
                            </p>
                          </div>
                        </div>

                        
                        {/* Test Cards Grid for Single Date */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {selectedTests.map(testName => {
                            const trend = trendsData[testName];
                            if (!trend || trend.dataPoints.length === 0) {
                              // Show loading placeholder if test is selected but data not loaded yet
                              return (
                                <div key={testName} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                                  <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                      {availableTests.find(t => t.testName === testName)?.displayName || testName}
                                    </h3>
                                  </div>
                                  <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="mt-2 text-sm text-gray-500">Loading data...</p>
                                  </div>
                                </div>
                              );
                            }
                            
                            // Find data point for selected date
                            const dataPoint = trend.dataPoints.find(dp => dp.date === selectedSingleDate);
                            if (!dataPoint) return null;
                            
                            // Find the actual index of this data point
                            const dataIndex = trend.dataPoints.findIndex(dp => dp.date === selectedSingleDate);
                            
                            return (
                              <div key={testName} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-lg font-medium text-gray-900">
                                    {trend.displayName}
                                  </h3>
                                  <div className="relative group">
                                    <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </button>
                                    {/* Tooltip */}
                                    <div className="absolute right-0 top-full mt-1 w-64 p-3 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                      <div className="font-medium mb-1">{trend.displayName}</div>
                                      <div className="text-xs text-gray-300">
                                        {getTestDescription(testName)}
                                      </div>
                                      <div className="absolute bottom-full right-2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                                {dataPoint.referenceMin !== null && dataPoint.referenceMax !== null ? (
                                  <SinglePointReferenceDisplay
                                    value={dataPoint.value}
                                    referenceMin={dataPoint.referenceMin}
                                    referenceMax={dataPoint.referenceMax}
                                    units={trend.units}
                                    testName={trend.displayName}
                                  />
                                ) : (
                                  <div className="text-center py-4">
                                    <p className="text-2xl font-bold text-gray-900">
                                      {dataPoint.value.toFixed(2)} {trend.units}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-2">
                                      {referenceRangeSource === 'lab' 
                                        ? 'Lab reference ranges not available'
                                        : 'Reference ranges not available'}
                                    </p>
                                  </div>
                                )}
                                <div className="mt-6 flex justify-center">
                                  <button
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      handleDataPointClick({
                                        dataIndex: dataIndex,
                                        value: dataPoint.value,
                                        date: dataPoint.date,
                                        testName: trend.testName,
                                        units: trend.units,
                                        referenceMin: dataPoint.referenceMin || undefined,
                                        referenceMax: dataPoint.referenceMax || undefined,
                                        status: dataPoint.status
                                      }, { 
                                        x: rect.left + rect.width / 2, 
                                        y: rect.top 
                                      });
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit Value
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  } else {
                    // Trends View (existing multi-date view)
                    return (
                      <>
                        {/* Insights Panel */}
                        {Object.keys(trendsData).length > 0 && (
                          <TrendsInsights
                            trendsData={trendsData}
                            dateRange={dateRange}
                          />
                        )}
                        
                        {/* Charts Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {selectedTests.map(testName => {
                    console.log(`🔍 Rendering chart for ${testName}:`, {
                      hasTrend: !!trendsData[testName],
                      dataPointCount: trendsData[testName]?.dataPoints?.length || 0,
                      trendsDataKeys: Object.keys(trendsData),
                      referenceRangeSource,
                      showReferenceRanges
                    });
                    const trend = trendsData[testName];
                    const isLoading = loadingTests.has(testName);
                    
                    if (!trend || trend.dataPoints.length === 0) {
                      return (
                        <div key={testName} className="bg-white rounded-lg shadow p-6">
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {availableTests.find(t => t.testName === testName)?.displayName || testName}
                          </h3>
                          {isLoading ? (
                            <div className="space-y-4">
                              {/* Chart skeleton */}
                              <div className="h-64 bg-gray-50 rounded-lg animate-pulse relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100 to-transparent animate-shimmer"></div>
                              </div>
                              {/* Stats skeleton */}
                              <div className="grid grid-cols-3 gap-2">
                                <div className="bg-gray-50 rounded p-2 h-16 animate-pulse"></div>
                                <div className="bg-gray-50 rounded p-2 h-16 animate-pulse"></div>
                                <div className="bg-gray-50 rounded p-2 h-16 animate-pulse"></div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500">No data available for the selected period</p>
                          )}
                        </div>
                      );
                    }

                    const chartData = prepareChartData(trend);
                    
                    // Debug: Log the chart data to see if reference ranges are present
                    console.log(`Chart data for ${testName}:`, {
                      ...chartData,
                      dataPointsWithRefs: trend.dataPoints.map(dp => ({
                        date: dp.date,
                        value: dp.value,
                        refMin: dp.referenceMin,
                        refMax: dp.referenceMax
                      }))
                    });
                    
                    // Check if lab ranges were requested but not available
                    const labRangesNotAvailable = referenceRangeSource === 'lab' && 
                      (!chartData.referenceMin || !chartData.referenceMax);
                    
                    return (
                      <div key={testName} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                        <div className="chart-container">
                          <EnhancedLineChart
                              key={`${testName}-${JSON.stringify(chartData.values)}-${trend.dataPoints.filter(dp => dp.validation).length}`} // Force re-render on data or validation change
                              title={trend.displayName}
                              testName={testName} // Pass actual test name for data lookups
                              data={chartData}
                              units={trend.units}
                              dataPoints={trend.dataPoints.map(dp => ({
                                date: dp.date,
                                value: dp.value,
                                units: dp.units,
                                referenceMin: dp.referenceMin,
                                referenceMax: dp.referenceMax,
                                status: dp.status,
                                autoFixed: dp.autoFixed,
                                originalValue: dp.originalValue,
                                originalUnits: dp.originalUnits,
                                chartValue: (dp as any).chartValue,
                                isOutlier: (dp as any).isOutlier,
                                outlierConfidence: (dp as any).outlierConfidence,
                                validation: dp.validation
                              }))}
                              onDataPointClick={handleDataPointClick}
                              onAcceptValidation={(dataIndex, value, unit) => 
                                handleAcceptValidation(testName, dataIndex, value, unit)
                              }
                              onDismissValidation={(dataIndex) =>
                                handleDismissValidation(testName, dataIndex)
                              }
                              onRequestReferenceRange={handleFetchReferenceRange}
                              isLoadingReference={loadingReferenceRanges.has(testName)}
                              referenceSourceDetails={referenceRangeDetails[testName]}
                              showReferenceRange={showReferenceRanges}
                              referenceRangeSource={referenceRangeSource}
                            />
                        </div>
                      </div>
                    );
                  })}
                        </div>
                      </>
                    );
                  }
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inline Value Editor */}
      {editingPoint && (
        <InlineValueEditor
          value={editingPoint.value}
          units={editingPoint.units}
          originalValue={editingPoint.originalValue}
          originalUnits={editingPoint.originalUnits}
          testName={editingPoint.testName}
          date={editingPoint.date}
          position={editingPoint.position}
          referenceRange={editingPoint.referenceRange}
          availableUnits={editingPoint.availableUnits}
          isSuspicious={editingPoint.isSuspicious}
          validationMessage={editingPoint.validationMessage}
          onSave={handleSaveEdit}
          onCancel={() => setEditingPoint(null)}
          onMarkAsCorrect={async () => {
            try {
              // Check auth first
              if (!user || !isAuthenticated) {
                console.error('No auth available');
                setNotification({
                  message: 'Authentication required. Please log in.',
                  type: 'error'
                });
                setTimeout(() => setNotification(null), 3000);
                return;
              }
              
              // Find the actual data point
              const trend = trendsData[editingPoint.testName];
              const dataPoint = trend?.dataPoints.find(dp => dp.date === editingPoint.date);
              
              if (!dataPoint?.id) {
                console.error('Cannot find data point to mark as correct');
                return;
              }
              
              // Call API to update the value (marking it as verified by keeping the same value)
              const response = await apiService.updateLabResult(dataPoint.id, {
                value: editingPoint.value,
                units: editingPoint.units
              });
              
              if (response.status === 'success') {
                // Mark this value as verified locally
                const newVerifiedValues = new Map(verifiedValues);
                const testSet = newVerifiedValues.get(editingPoint.testName) || new Set();
                testSet.add(editingPoint.date);
                newVerifiedValues.set(editingPoint.testName, testSet);
                setVerifiedValues(newVerifiedValues);
                
                // Update the data point to clear validation warnings
                const updatedDataPoints = trend.dataPoints.map(dp => 
                  dp.date === editingPoint.date
                    ? {
                        ...dp,
                        isSuspicious: false,
                        validation: undefined,
                        validationMessage: undefined,
                        isOutlier: false,
                        outlierConfidence: undefined
                      }
                    : dp
                );
                
                // Update trends data
                setTrendsData(prevTrends => ({
                  ...prevTrends,
                  [editingPoint.testName]: {
                    ...trend,
                    dataPoints: updatedDataPoints
                  }
                }));
                
                // Close the editor
                setEditingPoint(null);
                
                // Show success notification
                setNotification({
                  message: 'Value marked as correct. Warning removed.',
                  type: 'success'
                });
                
                // Clear notification after 3 seconds
                setTimeout(() => setNotification(null), 3000);
              }
            } catch (error) {
              console.error('Error marking value as correct:', error);
              setNotification({
                message: 'Failed to mark value as correct',
                type: 'error'
              });
              setTimeout(() => setNotification(null), 3000);
            }
          }}
        />
      )}

      {/* Unit Mismatch Warning */}
      {showUnitMismatchWarning && unitMismatches.has(showUnitMismatchWarning) && (
        <UnitMismatchWarning
          testName={showUnitMismatchWarning}
          conversions={unitMismatches.get(showUnitMismatchWarning)!.conversions}
          warnings={unitMismatches.get(showUnitMismatchWarning)!.warnings}
          onDismiss={() => setShowUnitMismatchWarning(null)}
          onCorrect={() => {
            // Find the first mislabeled data point
            const trend = trendsData[showUnitMismatchWarning];
            if (trend) {
              const mislabeledPoint = trend.dataPoints.find(dp => {
                if (dp.originalValue && dp.originalUnits) {
                  const detection = detectLikelyUnit(dp.originalValue, showUnitMismatchWarning, dp.originalUnits);
                  return detection.confidence === 'high' && detection.likelyUnit !== dp.originalUnits;
                }
                return false;
              });
              
              if (mislabeledPoint) {
                const detection = detectLikelyUnit(
                  mislabeledPoint.originalValue!, 
                  showUnitMismatchWarning, 
                  mislabeledPoint.originalUnits!
                );
                
                setShowCorrectionDialog({
                  testName: showUnitMismatchWarning,
                  dataPoint: mislabeledPoint,
                  unitMismatchInfo: {
                    detectedUnit: detection.likelyUnit,
                    reason: detection.reason || '',
                    suggestedValue: mislabeledPoint.value
                  }
                });
              }
            }
            setShowUnitMismatchWarning(null);
          }}
        />
      )}

      {/* Correction Dialog for Unit Mismatch */}
      {showCorrectionDialog && (
        <CorrectionDialog
          marker={showCorrectionDialog.testName}
          labValue={{
            value: showCorrectionDialog.dataPoint.originalValue || showCorrectionDialog.dataPoint.value,
            units: showCorrectionDialog.dataPoint.originalUnits || showCorrectionDialog.dataPoint.units,
            reference_range: showCorrectionDialog.dataPoint.referenceRange
          } as any}
          mode="unit"
          unitMismatchInfo={showCorrectionDialog.unitMismatchInfo}
          onCorrect={async (testName, newValue, newUnit) => {
            if (showCorrectionDialog.dataPoint.id) {
              await handleSaveEdit(newValue, newUnit);
            }
            setShowCorrectionDialog(null);
          }}
          onCancel={() => setShowCorrectionDialog(null)}
        />
      )}

      {/* Auto-Correction Manager */}
      {showCorrectionManager && (
        <AutoCorrectionManager
          corrections={(() => {
            const allCorrections: any[] = [];
            autoCorrectedValues.forEach((corrections, testName) => {
              // Filter by test if specified
              if (correctionManagerTestFilter && testName !== correctionManagerTestFilter) {
                return;
              }
              corrections.forEach(c => {
                allCorrections.push({
                  testName,
                  ...c
                });
              });
            });
            return allCorrections;
          })()}
          onAcceptAll={async () => {
            // Save all corrections to database and update UI
            const allCorrections: Array<{
              testName: string;
              date: string;
              value: number;
              units: string;
              dataPointId?: number;
            }> = [];
            
            // Collect all corrections
            autoCorrectedValues.forEach((corrections, testName) => {
              corrections.forEach(c => {
                const trend = trendsData[testName];
                if (trend) {
                  const dp = trend.dataPoints.find(d => d.date === c.date);
                  if (dp?.id) {
                    allCorrections.push({
                      testName,
                      date: c.date,
                      value: c.correctedValue,
                      units: c.correctedUnits,
                      dataPointId: dp.id
                    });
                  }
                }
              });
            });
            
            // Save to database
            if (user && isAuthenticated) {
              try {
                // Save each correction
                const savePromises = allCorrections
                  .filter(correction => correction.dataPointId !== undefined)
                  .map(correction => 
                    apiService.updateLabResult(correction.dataPointId!, {
                      value: correction.value,
                      unit: correction.units,
                      reason: 'Auto-correction accepted by user'
                    })
                  );
                
                await Promise.all(savePromises);
                
                // Update UI - remove autoFixed flags
                const updatedTrendsData = { ...trendsData };
                autoCorrectedValues.forEach((corrections, testName) => {
                  const trend = updatedTrendsData[testName];
                  if (trend) {
                    trend.dataPoints = trend.dataPoints.map(dp => {
                      const wasAutoCorrected = corrections.some(c => c.date === dp.date);
                      if (wasAutoCorrected) {
                        // Remove autoFixed flag but keep the corrected values
                        return {
                          ...dp,
                          autoFixed: false,
                          originalValue: undefined,
                          originalUnits: undefined
                        };
                      }
                      return dp;
                    });
                  }
                });
                
                setTrendsData(updatedTrendsData);
                
                // Clear auto-corrected values
                setAutoCorrectedValues(new Map());
                
                setNotification({
                  message: `${allCorrections.length} correction${allCorrections.length > 1 ? 's' : ''} accepted and saved`,
                  type: 'success'
                });
                setTimeout(() => setNotification(null), 3000);
              } catch (error) {
                console.error('Failed to save corrections:', error);
                setNotification({
                  message: 'Failed to save some corrections',
                  type: 'error'
                });
                setTimeout(() => setNotification(null), 5000);
              }
            }
          }}
          onRevertCorrection={async (testName, date) => {
            // Find the correction and revert it
            const trend = trendsData[testName];
            if (!trend) return;
            
            const dataPointIndex = trend.dataPoints.findIndex(dp => dp.date === date);
            if (dataPointIndex === -1) return;
            
            const dataPoint = trend.dataPoints[dataPointIndex];
            if (!dataPoint.originalValue || !dataPoint.originalUnits) return;
            
            // Update the data point back to original
            const updatedDataPoints = [...trend.dataPoints];
            updatedDataPoints[dataPointIndex] = {
              ...dataPoint,
              value: dataPoint.originalValue,
              units: dataPoint.originalUnits,
              autoFixed: false,
              originalValue: undefined,
              originalUnits: undefined
            };
            
            // Update trends data
            setTrendsData(prev => ({
              ...prev,
              [testName]: {
                ...trend,
                dataPoints: updatedDataPoints
              }
            }));
            
            // Remove from auto-corrected values
            setAutoCorrectedValues(prev => {
              const newMap = new Map(prev);
              const corrections = newMap.get(testName);
              if (corrections) {
                const filtered = corrections.filter(c => c.date !== date);
                if (filtered.length > 0) {
                  newMap.set(testName, filtered);
                } else {
                  newMap.delete(testName);
                }
              }
              return newMap;
            });
            
            // Call API to revert the correction
            if (dataPoint.id && user && isAuthenticated) {
              try {
                await apiService.updateLabResult(dataPoint.id, {
                  value: dataPoint.originalValue,
                  unit: dataPoint.originalUnits,
                  reason: 'Reverted auto-correction'
                });
              } catch (error) {
                console.error('Failed to revert correction in backend:', error);
              }
            }
            
            setNotification({
              message: `Reverted correction for ${testName} on ${new Date(date).toLocaleDateString()}`,
              type: 'success'
            });
            setTimeout(() => setNotification(null), 3000);
          }}
          onModifyCorrection={async (testName, date, newValue, newUnits) => {
            // Find the data point and update it
            const trend = trendsData[testName];
            if (!trend) return;
            
            const dataPointIndex = trend.dataPoints.findIndex(dp => dp.date === date);
            if (dataPointIndex === -1) return;
            
            const dataPoint = trend.dataPoints[dataPointIndex];
            
            // Update the data point
            const updatedDataPoints = [...trend.dataPoints];
            updatedDataPoints[dataPointIndex] = {
              ...dataPoint,
              value: newValue,
              units: newUnits,
              autoFixed: true
            };
            
            // Update trends data
            setTrendsData(prev => ({
              ...prev,
              [testName]: {
                ...trend,
                dataPoints: updatedDataPoints
              }
            }));
            
            // Update auto-corrected values
            setAutoCorrectedValues(prev => {
              const newMap = new Map(prev);
              const corrections = newMap.get(testName) || [];
              const correctionIndex = corrections.findIndex(c => c.date === date);
              if (correctionIndex >= 0) {
                corrections[correctionIndex] = {
                  ...corrections[correctionIndex],
                  correctedValue: newValue,
                  correctedUnits: newUnits
                };
                newMap.set(testName, [...corrections]);
              }
              return newMap;
            });
            
            // Call API to update the value
            if (dataPoint.id && user && isAuthenticated) {
              try {
                await apiService.updateLabResult(dataPoint.id, {
                  value: newValue,
                  unit: newUnits,
                  reason: 'Modified auto-correction'
                });
              } catch (error) {
                console.error('Failed to update correction in backend:', error);
              }
            }
            
            setNotification({
              message: `Updated correction for ${testName}`,
              type: 'success'
            });
            setTimeout(() => setNotification(null), 3000);
          }}
          onClose={() => {
            setShowCorrectionManager(false);
            setCorrectionManagerTestFilter(null);
          }}
        />
      )}

      {/* Floating AI Assistant Button */}
      <button
        onClick={() => setIsChatOpen(true)}
        className="fixed bottom-20 right-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 z-30 group flex items-center gap-3 px-5 py-3"
        title="Ask AI Assistant"
      >
        {/* AI Sparkle Icon */}
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
        <span className="font-medium">AI Assistant</span>
      </button>

      {/* AI Explanation Button (appears when editing a point) */}
      {editingPoint && (
        <div 
          className="fixed z-50 bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-blue-700 transition-colors"
          style={{
            left: editingPoint.position.x + 320, // Position to the right of the editor
            top: editingPoint.position.y
          }}
          onClick={() => {
            handleGetLLMExplanation(
              editingPoint.testName,
              editingPoint.value,
              editingPoint.units
            );
          }}
        >
          <div className="flex items-center gap-2 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            AI Explain
          </div>
        </div>
      )}

      {/* LLM Explanation Modal */}
      {selectedMarkerForExplanation && (
        <LLMExplanation
          explanation={llmExplanation}
          isLoading={isLoadingExplanation}
          selectedMarker={selectedMarkerForExplanation}
          error={explanationError}
          value={explanationValue}
          units={explanationUnits}
          onClose={() => {
            setSelectedMarkerForExplanation(null);
            setLlmExplanation(null);
            setExplanationError(null);
            setExplanationValue(undefined);
            setExplanationUnits(undefined);
          }}
        />
      )}

      {/* Floating Chat Modal */}
      <FloatingChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        testData={effectiveViewMode === 'single' ? getSingleDateData() : trendsData}
        viewType={effectiveViewMode === 'single' ? 'single' : 'trends'}
        selectedDate={selectedSingleDate || undefined}
      />

      {/* Medical Disclaimer Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-amber-50 border-t border-amber-200 p-2 text-center z-10">
        <p className="text-xs text-amber-800">
          <strong>Medical Disclaimer:</strong> This tool is for informational purposes only. 
          Always consult with your healthcare provider for medical interpretation and advice.
        </p>
      </div>

      {/* Notification - adjusted position to account for disclaimer */}
      {notification && (
        <div className={`fixed bottom-16 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : notification.type === 'warning'
            ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : notification.type === 'warning' ? (
              <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};